import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

import { TRADES_QUERY_KEY } from "./useTrades";
import { NOTES_QUERY_KEY } from "./useNotes";

type NoteResponse = components["schemas"]["NoteResponse"];

// Autosave a trade's single notepad note (ADR 0036 D2/D3). PUTs the raw body to
// the upsert route, which find-or-creates the trade's oldest live note and
// returns it -- or `null` when a blank (whitespace-only) body soft-deletes the
// note and the trade reverts to "no note". On settle it refreshes the notes
// list (so the bound editor re-reads the canonical note) and the trades list
// (the boolean "Note" chip tracks notes_count).
export function useUpsertTradeNote(tradeId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<NoteResponse | null, Error, string>({
    mutationFn: async (body) => {
      const { data, error } = await client.PUT("/api/trades/{trade_id}/note", {
        params: { path: { trade_id: tradeId } },
        body: { body },
      });
      if (error !== undefined) {
        throw new Error(`Failed to save the note on trade ${tradeId}`);
      }
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: TRADES_QUERY_KEY });
    },
  });
}
